'use client';

import { CampaignLink } from '@/components/CampaignLink';
import { beginInstantRouteTransition } from '@/components/InstantRouteTransitionOverlay';
import { AppIcon } from '@/icons/AppIcon';
import { HamburgerMenu } from '@/icons/HamburgerMenu';
import { getCampaignData } from '@/lib/BasketStorage';
import { CheckoutPage } from '@/lib/checkoutRoutes';
import { getIsAnonymous, getLocalUser, isAppClient, notifyUserData } from '@/lib/LocalStorage';
import { CLAW_PROMO_CODE_SESSION_KEY } from '@/lib/nativeAttributionPromoCode';
import { appendCampaignParamsToUrl } from '@/lib/persistCampaignParams';
import { safeSessionStorage } from '@/lib/safeStorage';
import { appRPC } from '@/lib/utils';
import { WebLink } from '@/lib/WebLinks';
import { WindowDispatchEvent } from '@/lib/WindowDispatchEvent';
import { MenuCategory } from '@/models/MenuCategories';
import { Product } from '@/models/Product';
import { SettingsHomepageItems, SettingsTopbar } from '@/models/Settings';
import InnerHTML from 'dangerously-set-html-content';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import CheckoutHeader from './CheckoutHeader';
import HeaderActions from './HeaderActions';
import HeaderLoadingBar from './HeaderLoadingBar';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const SearchBoxSkeleton = () => (
  <div className="h-12 w-full max-w-md rounded-full bg-white/15" aria-hidden="true" />
);

// SSR + height skeleton: avoids header growing after hydrate (main#main CLS).
const SearchBox = dynamic(() => import('./SearchBox'), {
  loading: () => <SearchBoxSkeleton />,
});
const CategoryNav = dynamic(() =>
  import('./category-nav').then((mod) => ({ default: mod.CategoryNav })),
);
const SidebarContent = dynamic(() => import('./SidebarContent'), { ssr: false });
const HeaderCategorySelector = dynamic(() => import('./HeaderCategorySelector'), { ssr: false });
const HeaderDistrictChooser = dynamic(() => import('./HeaderDistrictChooser'), { ssr: false });

export default function AppHeaderContent({
  items,
  menuCategories,
  products,
  topbar,
  isApp: isAppProp = false,
}: {
  items: SettingsHomepageItems;
  menuCategories: MenuCategory[];
  products: Product[];
  topbar?: SettingsTopbar;
  isApp?: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [isApp, setIsApp] = useState(isAppProp);
  const promoBarHeightValue = 40;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [topbarContent, setTopbarContent] = useState<React.ReactNode | null>(null);
  const [otherCityPathValue, setOtherCityPathValue] = useState<string | undefined | null>(null);
  const [onlySimple, setOnlySimple] = useState<boolean>(false);
  const [topbarHeight, setTopbarHeight] = useState<number>(0);
  const [promoBarHeight, setPromoBarHeight] = useState(promoBarHeightValue);
  const [isUserAnonymous, setUserIsAnonymous] = useState(true);
  // Uygulama içinde (native fullscreen webview) açılan sözleşme/profil sayfalarında
  // üstteki arama barını gizlemek için: hzc-web'e `?hideHeaderSearch=1` ile gelinir.
  const [hideHeaderSearch, setHideHeaderSearch] = useState(false);
  // Promo bant app içinde hiç görünmemeli (hydration ile flicker yapıyordu).
  const [showAppPromoBanner, setShowAppPromoBanner] = useState(isApp ? 0 : 1);

  const clearAppPromoCodeChip = () => {
    try {
      window.dispatchEvent(new CustomEvent('claw_clear_code'));
    } catch {
      // ignore
    }
    safeSessionStorage.removeItem(CLAW_PROMO_CODE_SESSION_KEY);
  };

  useEffect(() => {
    setIsApp(isAppClient());
    const onPlatform = () => setIsApp(isAppClient());
    window.addEventListener('platform:set', onPlatform);
    return () => window.removeEventListener('platform:set', onPlatform);
  }, []);

  useEffect(() => {
    const user = getLocalUser();
    if (user) {
      notifyUserData(user);
    }
  }, []);

  useEffect(() => {
    try {
      const value = new URLSearchParams(window.location.search).get('hideHeaderSearch');
      const hide = value === '1';
      setHideHeaderSearch(hide);
      if (hide) {
        document.documentElement.setAttribute('data-hide-header-search', '1');
      } else {
        document.documentElement.removeAttribute('data-hide-header-search');
      }
    } catch {
      // ignore
    }
  }, [pathname]);

  useEffect(() => {
    updateUIForApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topbarHeight, promoBarHeight, isApp]);

  const updateUIForApp = () => {
    document.documentElement.style.setProperty(
      '--hc-search-h',
      `${topbarHeight + 80 + promoBarHeight}px`,
    );
    document.documentElement.style.setProperty(
      '--hc-search-padding',
      `${topbarHeight + 72 + promoBarHeight}px`,
    );
    appRPC('bridge', 'updateHeaderHeight', { headerHeight: topbarHeight + 80 + promoBarHeight });
  };

  useEffect(() => {
    if (topbar?.barHeight && isApp) {
      setTopbarHeight(topbar.barHeight);
    } else {
      setTopbarHeight(0);
    }

    if (topbar) {
      setTopbarContent(topbar.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topbar]);

  const basketListener = () => {
    const isAnonymous = getIsAnonymous();
    setUserIsAnonymous(isAnonymous);

    // const basket = getBasket();
    const campaignData = getCampaignData();

    // console.log('BASKET UPDATED IN HEADER:', campaignData, isAnonymous);

    if (!campaignData) {
      setShowAppPromoBanner(0);
      setPromoBarHeight(promoBarHeightValue);
      if (isApp) clearAppPromoCodeChip();
      return;
    }

    setShowAppPromoBanner(campaignData.showAppPromoBanner || 0);

    setPromoBarHeight(0);

    if (isApp) {
      const shouldShow = Number(campaignData.showAppPromoBanner) === 1;
      if (shouldShow) {
        // No-op: native attribution promo code should come from backend (not hardcoded).
      } else {
        clearAppPromoCodeChip();
      }
    }
    return;

    // if (isAnonymous) {
    //   setPromoBarHeight(promoBarHeightValue);
    //   return;
    // }

    // const shouldShow =
    //   !!campaignData.userCampaignData?.hasMobileDevice &&
    //   !campaignData.userCampaignData?.hasMobilePurchase;

    // setPromoBarHeight(shouldShow ? promoBarHeightValue : 0);
  };

  useEffect(() => {
    basketListener();

    window.addEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
    return () => window.removeEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
  }, []);

  useEffect(() => {
    basketListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const anonListener = () => basketListener();
    window.addEventListener(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE, anonListener);
    return () =>
      window.removeEventListener(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE, anonListener);
  }, []);

  useEffect(() => {
    // if (pathname === '/hediye-202506') {
    //   setOnlySimple(true);
    //   return;
    // }
    // const campaignName = getCampaignName();
    // if (campaignName === 'hediye-202506') {
    //   if (pathname === '/' || pathname === '') {
    //     router.replace('/hediye-202506');
    //     return;
    //   }
    //   setOnlySimple(true);
    //   return;
    // }

    if (otherCityPathValue) {
      const otherCityPath = otherCityPathValue.startsWith('/')
        ? otherCityPathValue
        : `/${otherCityPathValue}`;
      if (otherCityPath !== pathname && pathname == '/') {
        setOtherCityPathValue(undefined);
        router.replace(appendCampaignParamsToUrl(otherCityPath));
      }
    }
  }, [otherCityPathValue, pathname, router]);

  if (
    hideHeaderSearch ||
    Object.values(CheckoutPage)
      .map((item) => `/${item}`)
      .includes(pathname) ||
    pathname === WebLink.ORDER_RECEIVED
  ) {
    return <CheckoutHeader isApp={isApp} />;
  }

  const districtListener = (otherCityPath: string | undefined | null) => {
    if (otherCityPath) {
      setOtherCityPathValue(otherCityPath);
    }
  };

  const onDistrictChange = (districtUrl: string | undefined | null) => {
    if (districtUrl && !pathname.startsWith('/urun/')) {
      const href = appendCampaignParamsToUrl(districtUrl);
      // Soft-nav to /bolge/* often waits on middleware + RSC before painting;
      // show listing skeleton immediately so the user sees navigation started.
      beginInstantRouteTransition(href);
      router.push(href);
    }
  };

  const bannerClickHandler = (url: string | undefined | null) => {
    if (!url) return;
    if (isApp) {
      const RPCcheck = appRPC('route', 'navigateWithUrl', { url, target: 'home' });
      if (!RPCcheck && window) {
        window.location.href = url;
        return;
      } else if (RPCcheck) {
        return;
      }
    }
    const href = appendCampaignParamsToUrl(url);
    beginInstantRouteTransition(href);
    router.replace(href);
  };

  const appGotoLogin = () => {
    appRPC('route', 'navigateWithUrl', { url: WebLink.LOGIN, target: 'user' });
  };

  return (
    <header
      className="hc-search-sticky sticky top-0 z-50 isolation-isolate transition-colors duration-300 ease-in-out bg-hzc_purple_500 flex w-full border-b-1 border-[#212665]"
      id="hc-search"
    >
      <div className="relative overflow-visible flex flex-col w-full my-auto">
        {/*{showAppPromoBanner == 1 && !isApp && (
          <div className="bg-[#AD131A] h-[40px] text-white flex items-center justify-center text-xs px-4 sm:hidden text-center">
            <CampaignLink href="https://hzc.onelink.me/HRQo/app">
              Uygulamayı indir, 150 TL indirimden faydalan
            </CampaignLink>
          </div>
        )}
        */}
        {/* App içinde promo bant gösterme: native/app tarafı kendi kampanya yüzeyini yönetiyor */}
        {topbar && !onlySimple && (
          <InnerHTML
            id="top-banner"
            style={{
              height: `${topbar.barHeight}px`,
              backgroundColor: topbar.backgroundColor,
              color: topbar.textColor,
              cursor: topbar.url ? 'pointer' : 'default',
            }}
            className="w-full flex items-center justify-center"
            html={`${topbarContent || ' '}`}
            allowRerender={true}
            onClick={() => {
              bannerClickHandler(topbar.url);
            }}
          />
        )}
        {!onlySimple && (
          <div className="hidden lg:flex items-center justify-between bg-hzc_purple_600 lg:px-6 px-3.5 min-h-[40px]">
            <HeaderCategorySelector homePageItems={items} />
            <HeaderDistrictChooser
              districtListener={districtListener}
              onDistrictChange={onDistrictChange}
            />
          </div>
        )}
        {
          // Large Header
          <div className="w-full flex flex-col">
            <div className="hidden lg:flex h-24 w-full max-w-7xl mx-auto relative">
              <nav className="md:px-6 lg:px-0 my-auto w-full">
                <div className="flex flex-wrap justify-between w-full md:px-4 2xl:px-0">
                  {!onlySimple && (
                    <div className="flex flex-col gap-2">
                      <SearchBox isApp={isApp} />
                    </div>
                  )}
                  <CampaignLink href="/" className="flex items-center">
                    <AppIcon className="h-12 self-center" />
                  </CampaignLink>
                  <HeaderActions />
                </div>
              </nav>
            </div>
            {!onlySimple && (
              <div className="hidden lg:flex flex-col gap-2 items-center pb-4 min-h-[52px]">
                <CategoryNav categories={menuCategories || []} products={products || []} />
              </div>
            )}
          </div>
        }

        {
          // Mobile header
          <div className="lg:hidden py-4">
            <nav className="px-3.5 my-auto flex flex-col gap-4">
              {!isApp && (
                <div className="flex justify-between">
                  <div className="flex">
                    {!onlySimple && (
                      <Sheet key="menuSheet" open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                        <SheetTrigger asChild>
                          <Button
                            className="text-center flex justify-center hover:cursor-pointer bg-transparent hover:bg-transparent mr-2"
                            size="icon"
                            aria-label="Menü"
                          >
                            <HamburgerMenu className="lg:hidden text-white text-2xl my-auto" />
                          </Button>
                        </SheetTrigger>
                        <SheetContent
                          side="left"
                          className="px-0 bg-hzc_purple_500"
                          closeEnabled={false}
                        >
                          {isSidebarOpen ? (
                            <SidebarContent
                              setIsSidebarOpen={setIsSidebarOpen}
                              menuCategories={menuCategories}
                              isApp={isApp}
                            />
                          ) : null}
                        </SheetContent>
                      </Sheet>
                    )}
                    <CampaignLink href="/" className="flex" aria-label="Ana sayfa">
                      <AppIcon className="h-9 w-32 self-center" />
                    </CampaignLink>
                  </div>
                  <HeaderActions />
                </div>
              )}
              {!onlySimple && <SearchBox isApp={isApp} />}
            </nav>
          </div>
        }
      </div>
      <Suspense fallback={null}>
        <HeaderLoadingBar enabled={isApp} />
      </Suspense>
      {isApp && <div className="hc-search-spacer" />}
    </header>
  );
}
